import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
import { compressImage } from "../utils/imageCompressor";
import { apiFetch } from "../lib/api";

// ─── SHARED CLAUDE VISION CALL ────────────────────────────────────────────────
async function callClaudeVision(imgData, prompt) {
  const res = await apiFetch("claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 700,
      messages: [{ role: "user", content: [
        { type: "image", source: { type: "base64", media_type: "image/jpeg", data: imgData } },
        { type: "text", text: prompt },
      ]}],
    }),
  });
  const data = await res.json();
  const text = data.content?.[0]?.text || "";
  const m = text.match(/\{[\s\S]*\}/);
  if (m) return JSON.parse(m[0]);
  throw new Error("No JSON in response");
}

// ─── CAMERA HELPER HOOK ───────────────────────────────────────────────────────
function useCameraCapture() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const stopStream = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; };
  useEffect(() => () => stopStream(), []); // eslint-disable-line

  const startCamera = async (setMode, setError) => {
    setError("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); }
      setMode("camera");
    } catch { setError("Camera not available. Use Upload or Drag & Drop instead."); }
  };

  const captureFrame = (setImgData, setMode) => {
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setImgData(c.toDataURL("image/jpeg", 0.85).split(",")[1]);
    stopStream(); setMode("preview");
  };

  return { videoRef, canvasRef, stopStream, startCamera, captureFrame };
}

async function fileToBase64(file) {
  const compressed = file.type.startsWith("image/") ? (await compressImage(file, 1400, 0.85) || file) : file;
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = ev => resolve(ev.target.result.split(",")[1]);
    reader.readAsDataURL(compressed);
  });
}

// ─── FACESHEET CAPTURE MODAL ──────────────────────────────────────────────────
export function FacesheetCaptureModal({ onClose, onParsed }) {
  const [mode, setMode] = useState("choose"); // choose | camera | preview
  const [imgData, setImgData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const dropRef = useRef(null);
  const { videoRef, canvasRef, stopStream, startCamera, captureFrame } = useCameraCapture();

  const handleFile = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setImgData(b64); setMode("preview");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dropRef.current && (dropRef.current.style.borderColor = "var(--border-2)");
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const processImage = async () => {
    setProcessing(true); setError("");
    try {
      const prompt = `This is a patient facesheet or intake document from a medical office. Extract patient demographic and insurance information and return ONLY a valid JSON object with these exact keys (use empty string "" if not found):
{
  "first_name": "",
  "last_name": "",
  "date_of_birth": "YYYY-MM-DD format if found, else empty string",
  "gender": "M or F or Other, else empty string",
  "phone": "10 digits only, no formatting",
  "email": "",
  "address": "street address only",
  "city": "",
  "state": "2-letter abbreviation",
  "zip": "5 digits",
  "insurance_name": "insurance carrier name",
  "insurance_member_id": "",
  "insurance_group": "",
  "subscriber_name": "full name of primary insured if different from patient",
  "subscriber_dob": "YYYY-MM-DD or empty",
  "emergency_contact_name": "",
  "emergency_contact_phone": "10 digits only",
  "referring_provider": "referring physician name",
  "primary_care_provider": "PCP name"
}
Return only valid JSON, no explanation or markdown.`;
      const parsed = await callClaudeVision(imgData, prompt);
      onParsed(parsed, "facesheet");
      onClose();
    } catch {
      setError("Could not extract data. Try a clearer image or enter manually.");
    }
    setProcessing(false);
  };

  const S = MS;
  return createPortal(
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.title}>📄 Scan Facesheet</span>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {mode === "choose" && (
          <div style={{ ...S.body, display: "flex", flexDirection: "column", gap: 10 }}>
            {error && <div style={S.errorBox}>{error}</div>}
            <p style={{ color: "var(--text-3)", fontSize: 13, margin: "0 0 4px" }}>
              Capture a patient facesheet to auto-populate the registration form:
            </p>
            <button style={S.optBtn} onClick={() => startCamera(setMode, setError)}>
              <span style={S.optIcon}>📷</span>
              <div><div style={S.optLabel}>Take Photo</div><div style={S.optHint}>Point camera at facesheet</div></div>
            </button>
            <label style={{ ...S.optBtn, cursor: "pointer" }}>
              <span style={S.optIcon}>🖼</span>
              <div><div style={S.optLabel}>Upload File</div><div style={S.optHint}>Image or PDF from device</div></div>
              <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
            </label>
            <div
              ref={dropRef}
              style={{ ...S.optBtn, border: "2px dashed var(--border-2)" }}
              onDragOver={e => { e.preventDefault(); dropRef.current.style.borderColor = "var(--teal)"; }}
              onDragLeave={() => dropRef.current && (dropRef.current.style.borderColor = "var(--border-2)")}
              onDrop={handleDrop}
            >
              <span style={S.optIcon}>⬇</span>
              <div><div style={S.optLabel}>Drag & Drop</div><div style={S.optHint}>Drop image or PDF here</div></div>
            </div>
          </div>
        )}

        {mode === "camera" && (
          <div style={{ ...S.body, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <video ref={videoRef} style={{ width: "100%", borderRadius: 8, background: "#000", maxHeight: 320, objectFit: "contain" }} autoPlay playsInline muted />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            {error && <div style={S.errorBox}>{error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.cancelBtn} onClick={() => { stopStream(); setMode("choose"); }}>← Back</button>
              <button style={S.primaryBtn} onClick={() => captureFrame(setImgData, setMode)}>📸 Capture</button>
            </div>
          </div>
        )}

        {mode === "preview" && (
          <div style={{ ...S.body, display: "flex", flexDirection: "column", gap: 14 }}>
            <img src={`data:image/jpeg;base64,${imgData}`} alt="Preview" style={{ width: "100%", borderRadius: 8, maxHeight: 300, objectFit: "contain", background: "#000" }} />
            {error && <div style={S.errorBox}>{error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.cancelBtn} onClick={() => setMode("choose")}>← Retake</button>
              <button style={{ ...S.primaryBtn, flex: 1 }} onClick={processImage} disabled={processing}>
                {processing ? "⏳ Extracting Data..." : "✓ Extract Patient Info"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── ID & INSURANCE CAPTURE MODAL ─────────────────────────────────────────────
export function IDInsuranceCaptureModal({ onClose, onParsed }) {
  const [step, setStep] = useState("dl"); // dl | insurance
  const [dlImg, setDlImg] = useState(null);
  const [insImg, setInsImg] = useState(null);
  const [dlData, setDlData] = useState(null);
  const [mode, setMode] = useState("choose"); // choose | camera | preview
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const { videoRef, canvasRef, stopStream, startCamera, captureFrame } = useCameraCapture();

  const isIns = step === "insurance";
  const currentImg = isIns ? insImg : dlImg;
  const setCurrentImg = isIns ? setInsImg : setDlImg;

  const handleFile = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setCurrentImg(b64); setMode("preview");
  };

  const processStep = async () => {
    setProcessing(true); setError("");
    try {
      if (!isIns) {
        const prompt = `This is the front of a US driver's license or state ID. Extract information and return ONLY a valid JSON object (empty string if not found):
{
  "first_name": "",
  "last_name": "",
  "date_of_birth": "YYYY-MM-DD",
  "address": "street address only",
  "city": "",
  "state": "2-letter abbreviation",
  "zip": "5 digits",
  "license_number": "",
  "expiry_date": "YYYY-MM-DD"
}
Return only valid JSON, no explanation or markdown.`;
        const parsed = await callClaudeVision(dlImg, prompt);
        setDlData(parsed);
        setMode("choose");
        setStep("insurance");
      } else {
        const prompt = `This is a health insurance card (front or back). Extract information and return ONLY a valid JSON object (empty string if not found):
{
  "insurance_name": "insurance company name",
  "plan_name": "plan or product name",
  "member_id": "member ID number",
  "group_number": "",
  "subscriber_name": "name of primary insured",
  "phone_number": "member services number, 10 digits only",
  "copay": "copay dollar amount, numbers only",
  "deductible": "deductible dollar amount, numbers only"
}
Return only valid JSON, no explanation or markdown.`;
        const insData = await callClaudeVision(insImg, prompt);
        onParsed({ dl: dlData, insurance: insData }, "id-insurance");
        onClose();
      }
    } catch {
      setError(isIns ? "Could not read insurance card. Try a clearer photo." : "Could not read driver's license. Try a clearer photo.");
    }
    setProcessing(false);
  };

  const S = MS;
  return createPortal(
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <div style={{ flex: 1 }}>
            <span style={S.title}>🪪 {isIns ? "Insurance Card" : "Driver's License"}</span>
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>
              Step {isIns ? "2" : "1"} of 2 — {isIns ? "Scan insurance card" : "Scan driver's license front"}
            </div>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Step tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "0 20px" }}>
          {["Driver's License", "Insurance Card"].map((lbl, i) => {
            const active = i === 0 ? step === "dl" : step === "insurance";
            const done = i === 0 && dlData;
            return (
              <div key={i} style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, color: active ? "var(--teal)" : "var(--text-3)", borderBottom: active ? "2px solid var(--teal)" : "2px solid transparent", marginBottom: -1 }}>
                {done ? "✓ " : ""}{lbl}
              </div>
            );
          })}
        </div>

        {mode === "choose" && (
          <div style={{ ...S.body, display: "flex", flexDirection: "column", gap: 10 }}>
            {error && <div style={S.errorBox}>{error}</div>}
            <p style={{ color: "var(--text-3)", fontSize: 13, margin: "0 0 4px" }}>
              Capture {isIns ? "front of insurance card" : "front of driver's license"}:
            </p>
            <button style={S.optBtn} onClick={() => startCamera(setMode, setError)}>
              <span style={S.optIcon}>📷</span>
              <div><div style={S.optLabel}>Take Photo</div><div style={S.optHint}>Point camera at {isIns ? "insurance card" : "driver's license"}</div></div>
            </button>
            <label style={{ ...S.optBtn, cursor: "pointer" }}>
              <span style={S.optIcon}>🖼</span>
              <div><div style={S.optLabel}>Upload Photo</div><div style={S.optHint}>Choose from files</div></div>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
            </label>
            {isIns && (
              <button
                style={{ ...S.cancelBtn, fontSize: 12, marginTop: 4 }}
                onClick={() => { onParsed({ dl: dlData, insurance: {} }, "id-insurance"); onClose(); }}
              >
                Skip insurance card → use DL only
              </button>
            )}
          </div>
        )}

        {mode === "camera" && (
          <div style={{ ...S.body, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <video ref={videoRef} style={{ width: "100%", borderRadius: 8, background: "#000", maxHeight: 280, objectFit: "contain" }} autoPlay playsInline muted />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            {error && <div style={S.errorBox}>{error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.cancelBtn} onClick={() => { stopStream(); setMode("choose"); }}>← Back</button>
              <button style={S.primaryBtn} onClick={() => captureFrame(setCurrentImg, setMode)}>📸 Capture</button>
            </div>
          </div>
        )}

        {mode === "preview" && (
          <div style={{ ...S.body, display: "flex", flexDirection: "column", gap: 14 }}>
            <img src={`data:image/jpeg;base64,${currentImg}`} alt="Preview" style={{ width: "100%", borderRadius: 8, maxHeight: 240, objectFit: "contain", background: "#000" }} />
            {error && <div style={S.errorBox}>{error}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.cancelBtn} onClick={() => setMode("choose")}>← Retake</button>
              <button style={{ ...S.primaryBtn, flex: 1 }} onClick={processStep} disabled={processing}>
                {processing ? "⏳ Extracting..." : isIns ? "✓ Extract & Finish" : "✓ Extract & Continue →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── PENDING INTAKES PANEL ────────────────────────────────────────────────────
export function PendingIntakesPanel({ onClose, onCreateChart }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    db
      .from("intake_submissions")
      .select("id, created_at, form_data, status, portal_user_id, patient_id")
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => { setSubmissions(data || []); setLoading(false); });
  }, []);

  const S = MS;
  return createPortal(
    <div style={S.overlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <div style={{ flex: 1 }}>
            <span style={S.title}>📋 Pending Portal Intakes</span>
            {!loading && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>{submissions.length} submission{submissions.length !== 1 ? "s" : ""} awaiting chart creation</div>}
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...S.body, padding: 0, maxHeight: 460, overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>Loading...</div>
          ) : submissions.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              No pending intake submissions
            </div>
          ) : submissions.map(sub => {
            const fd = sub.form_data || {};
            const name = [fd.first_name, fd.last_name].filter(Boolean).join(" ") || "Unknown Patient";
            const dob = fd.date_of_birth || "";
            const fieldCount = Object.values(fd).filter(v => v && v !== "").length;
            const pct = Math.min(100, Math.round((fieldCount / 18) * 100));
            return (
              <div key={sub.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--teal-lt)", color: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                  {(fd.first_name?.[0] || "?")}{(fd.last_name?.[0] || "")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)" }}>{name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                    {dob && `DOB: ${dob} · `}
                    {new Date(sub.created_at).toLocaleDateString()} · {pct}% complete
                  </div>
                </div>
                <button
                  style={{ ...S.primaryBtn, whiteSpace: "nowrap", padding: "6px 14px", fontSize: 12 }}
                  onClick={() => { onCreateChart(sub); onClose(); }}
                >
                  Create Chart →
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── PENDING INTAKES DASHBOARD WIDGET ────────────────────────────────────────
export function PendingIntakesDashWidget({ onNavigate }) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    db
      .from("intake_submissions")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending")
      .then(({ count: c }) => setCount(c ?? 0));
  }, []);

  if (count === null || count === 0) return null;

  return (
    <div
      className="stat-card stat-card-link"
      style={{ cursor: "pointer", border: "1px solid var(--teal)", background: "var(--teal-lt)" }}
      onClick={() => onNavigate("new-patient", { intakeMethod: "portal" })}
    >
      <div className="stat-label" style={{ color: "var(--teal)" }}>Pending Intakes</div>
      <div className="stat-value" style={{ color: "var(--teal)" }}>{count}</div>
      <div className="stat-sub">Portal forms → create charts</div>
    </div>
  );
}

// ─── SHARED MODAL STYLES ──────────────────────────────────────────────────────
const MS = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal:     { background: "var(--navy-2)", border: "1px solid var(--border)", borderRadius: 12, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  header:    { display: "flex", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  title:     { fontSize: 15, fontWeight: 700, color: "var(--text-1)", flex: 1 },
  closeBtn:  { background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 4 },
  body:      { padding: 20, overflowY: "auto", flex: 1 },
  optBtn:    { display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 8, background: "var(--navy-3)", border: "1px solid var(--border)", cursor: "pointer", width: "100%", textAlign: "left" },
  optIcon:   { fontSize: 22, flexShrink: 0 },
  optLabel:  { fontWeight: 600, fontSize: 13, color: "var(--text-1)" },
  optHint:   { fontSize: 11, color: "var(--text-3)", marginTop: 2 },
  errorBox:  { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#f87171" },
  primaryBtn:{ padding: "9px 18px", borderRadius: 7, border: "none", background: "var(--teal)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  cancelBtn: { padding: "9px 14px", borderRadius: 7, border: "1px solid var(--border-2)", background: "transparent", color: "var(--text-2)", cursor: "pointer", fontSize: 13 },
};
